import { track } from '@affine/track';
import type {
  NavigationArgs,
  UiObjectRef,
  WorkspaceNavigationDescriptor,
  WorkspaceNavigationOutcome,
  WorkspaceNavigationRequest,
  WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import { isWithinWorkspaceViewCodePointLimit } from '@streamlain/copilot-realtime-contract';
import { LiveData, Service } from '@toeverything/infra';
import { nanoid } from 'nanoid';
import type { Observable } from 'rxjs';

import type { AffineCommand } from '../../../commands/registry/command';
import type { DocsService } from '../../doc';
import {
  buildDocsSearchNavigation,
  createDocsSearchNavigationResolvers,
  type DocsSearchNavigablePayload,
  type DocsSearchNavigation,
  openDocsSearchNavigation,
} from '../../docs-search/services/source-navigation';
import type { NavigationPanelRevealService } from '../../navigation-panel';
import { mapWorkspaceContextIssueToMessageKey } from '../../streamlain/copilot/workspace-context-messages';
import {
  EMPTY_NAVIGATION_ARGS,
  navigationActionId,
  NavigationActivationError,
  type UiNavigationDefinition,
} from '../../streamlain/copilot/workspace-navigation-catalog';
import { fitWorkspaceViewSnapshot } from '../../streamlain/copilot/workspace-view-budget';
import { WorkspaceViewContextService } from '../../streamlain/copilot/workspace-view-context';
import {
  FEATURE_KEY_COLLECTIONS,
  FEATURE_KEY_TAGS,
  isProductFeatureAllowed,
} from '../../streamlain/hooks/product-feature-gates';
import { requestWorkspacePaneSidebar } from '../../streamlain/workspace-pane/sidebar-request';
import type { WorkbenchService } from '../../workbench';
import type { WorkspaceService } from '../../workspace';
import { CollectionsQuickSearchSession } from '../impls/collections';
import { CommandsQuickSearchSession } from '../impls/commands';
import { CreationQuickSearchSession } from '../impls/creation';
import { DocsQuickSearchSession } from '../impls/docs';
import { LinksQuickSearchSession } from '../impls/links';
import { MailQuickSearchSession } from '../impls/mail';
import { isPersonDocItem, PeopleQuickSearchSession } from '../impls/people';
import { RecentDocsQuickSearchSession } from '../impls/recent-docs';
import { TagsQuickSearchSession } from '../impls/tags';
import {
  QUICK_SEARCH_RESULT_OWNER,
  type QuickSearchItem,
  quickSearchResultDefinitionId,
} from '../types/item';
import type { AdvancedSearchService } from './advanced-search';
import { openDocAtPerson } from './person-navigation';
import type { QuickSearchService } from './quick-search';

export type CMDKQuickSearchProductFeatures = {
  collectionsEnabled?: boolean;
  tagsEnabled?: boolean;
};

export class CMDKQuickSearchService extends Service {
  /** Render ACK from the existing compact result owner, not another result store. */
  readonly contentResultsGeneration$ = new LiveData<{
    scope: WorkspaceViewScope;
    generation: number;
  } | null>(null);
  constructor(
    private readonly advancedSearchService: AdvancedSearchService,
    private readonly quickSearchService: QuickSearchService,
    private readonly workbenchService: WorkbenchService,
    private readonly docsService: DocsService,
    private readonly navigationPanelRevealService: NavigationPanelRevealService,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
  }

  toggle(productFeatures: CMDKQuickSearchProductFeatures = {}) {
    if (this.advancedSearchService.open$.value) {
      this.close();
    } else if (this.quickSearchService.quickSearch.show$.value) {
      this.close();
    } else if (this.advancedSearchService.prefersAdvanced()) {
      this.quickSearchService.personFilter$.next(null);
      this.openAdvanced();
    } else {
      this.quickSearchService.personFilter$.next(null);
      this.openCompact('', productFeatures);
    }
  }

  /** The normal query owner, shared by the visible entry and native navigation. */
  applySearchQuery(
    args: NavigationArgs,
    features: CMDKQuickSearchProductFeatures = {}
  ) {
    const query = args.query ?? '';
    if (
      typeof query !== 'string' ||
      !isWithinWorkspaceViewCodePointLimit(query, 200)
    )
      return;
    if (
      this.advancedSearchService.open$.value ||
      this.advancedSearchService.prefersAdvanced()
    )
      this.openAdvanced(query, features);
    else if (this.quickSearchService.quickSearch.show$.value)
      this.quickSearchService.quickSearch.setQuery(query);
    else this.openCompact(query, features);
  }

  /** Bounded metadata for the actual displayed content rows; no query or callback copy. */
  createContentNavigation(
    scope: WorkspaceViewScope,
    items: Iterable<QuickSearchItem>,
    label: (item: QuickSearchItem) => string
  ): readonly UiNavigationDefinition[] {
    const quick = this.quickSearchService.quickSearch;
    const generation = quick.queryGeneration$.value;
    if (
      generation === null ||
      quick.isLoading$.value ||
      !quick.isSettled$.value
    )
      return [];
    const context = this.framework.get(WorkspaceViewContextService);
    const person = this.quickSearchService.personFilter$.value;
    const definitions: UiNavigationDefinition[] = [];
    const currentQuery = () =>
      context.isActiveScope(scope) &&
      quick.show$.value &&
      !quick.isLoading$.value &&
      quick.isSettled$.value &&
      quick.queryGeneration$.value === generation &&
      this.quickSearchService.personFilter$.value === person;
    let visits = 0;
    for (const item of items) {
      if (visits++ >= 128 || definitions.length >= 12) break;
      if (
        !isWithinWorkspaceViewCodePointLimit(item.id, 256) ||
        !this.isContentNavigable(item)
      )
        continue;
      const title = label(item);
      if (!isWithinWorkspaceViewCodePointLimit(title, 256) || !title.trim())
        continue;
      const available = () => {
        if (!currentQuery()) return false;
        let visited = 0;
        for (const current of quick.items$.value) {
          if (visited++ >= 128) break;
          if (current === item) return this.isContentNavigable(item);
        }
        return false;
      };
      definitions.push({
        id: quickSearchResultDefinitionId(generation, item.id),
        label: title,
        keywords: [],
        parentIds: [],
        argsSchema: EMPTY_NAVIGATION_ARGS,
        available,
        activate: () => {
          if (!available()) throw new NavigationActivationError('stale');
          quick.submit(item);
        },
        readTarget: () => {
          // The normal callback closes CMDK and the genuine destination publishes its refs.
          if (quick.show$.value || !context.isActiveScope(scope)) return null;
          const read = context.readActive();
          if (!read.ok) return null;
          const snapshot = read.snapshot;
          const payload = item.payload;
          let expected: UiObjectRef | null = null;
          if (
            item.source === 'docs' ||
            item.source === 'recent-doc' ||
            item.source === 'link'
          ) {
            let navigation: DocsSearchNavigation | null;
            try {
              navigation =
                item.source === 'link'
                  ? {
                      kind: 'doc',
                      docId: payload.docId,
                      blockIds: payload.blockIds,
                      elementIds: payload.elementIds,
                    }
                  : buildDocsSearchNavigation(
                      payload as DocsSearchNavigablePayload,
                      createDocsSearchNavigationResolvers(this.docsService)
                    );
            } catch {
              return null;
            }
            if (!navigation) return null;
            const docId = navigation.docId;
            if (person && item.source !== 'link') {
              // The normal person-filter callback opens the document at that person's cursor.
              expected = { kind: 'doc', id: docId, docId };
            } else if (navigation.kind === 'attachment') {
              expected = {
                kind: 'attachment',
                id: navigation.attachmentBlockId,
                docId,
              };
            } else {
              expected = navigation.fullscreenDatabaseRowId
                ? {
                    kind: 'database-row',
                    id: navigation.fullscreenDatabaseRowId,
                    docId,
                  }
                : navigation.blockIds?.[0]
                  ? { kind: 'block', id: navigation.blockIds[0], docId }
                  : navigation.elementIds?.[0]
                    ? {
                        kind: 'canvas-element',
                        id: navigation.elementIds[0],
                        docId,
                      }
                    : navigation.fullscreenDatabaseId
                      ? {
                          kind: 'block',
                          id: navigation.fullscreenDatabaseId,
                          docId,
                        }
                      : { kind: 'doc', id: docId, docId };
            }
          } else if (item.source === 'mail') {
            expected = { kind: 'mail-message', id: payload.messageId };
          } else if (item.source === 'collections') {
            expected = { kind: 'collection', id: payload.collectionId };
          } else if (item.source === 'tags') {
            expected = { kind: 'tag', id: payload.tagId };
          }
          if (!expected) return null;
          const { kind, id, docId } = expected;
          const focusRef = snapshot.refs.find(
            ref =>
              ref.kind === kind &&
              ref.id === id &&
              (!docId ||
                ref.docId === docId ||
                (ref.kind === 'doc' && ref.id === docId))
          );
          if (
            !focusRef ||
            (item.source === 'mail' && snapshot.route !== payload.route)
          )
            return null;
          return {
            ...scope,
            revision: snapshot.revision,
            surfaceId: snapshot.surfaceId,
            route: snapshot.route,
            title: snapshot.title,
            focusRef,
          };
        },
      });
    }
    return definitions;
  }

  private isContentNavigable(item: QuickSearchItem): boolean {
    if (item.disabled) return false;
    if (item.source === 'docs' || item.source === 'recent-doc') {
      const record = this.docsService.list.doc$(item.payload?.docId).value;
      if (!record || record.trash$.value) return false;
      // A row that keeps the list open ("Show N more") is not a destination.
      if (item.beforeSubmit) return false;
      const payload = item.payload as DocsSearchNavigablePayload;
      try {
        return (
          !!payload &&
          !!buildDocsSearchNavigation(
            payload,
            createDocsSearchNavigationResolvers(this.docsService)
          )
        );
      } catch {
        // Incomplete row locators remain ordinary source rows, never executable AI offers.
        return false;
      }
    }
    if (item.source === 'mail')
      return !!item.payload?.messageId && !!item.payload?.route;
    if (item.source === 'link') {
      const doc = this.docsService.list.doc$(item.payload?.docId).value;
      return !!doc && !doc.trash$.value;
    }
    return (
      (item.source === 'collections' &&
        isProductFeatureAllowed(FEATURE_KEY_COLLECTIONS) &&
        !!item.payload?.collectionId) ||
      (item.source === 'tags' &&
        isProductFeatureAllowed(FEATURE_KEY_TAGS) &&
        !!item.payload?.tagId)
    );
  }

  async searchNavigable(
    query: string,
    scope: WorkspaceViewScope,
    limit: number
  ): Promise<readonly WorkspaceNavigationDescriptor[]> {
    const context = this.framework.get(WorkspaceViewContextService);
    const quick = this.quickSearchService.quickSearch;
    if (
      !context.isActiveScope(scope) ||
      !quick.show$.value ||
      quick.isLoading$.value ||
      !quick.isSettled$.value ||
      quick.query$.value !== query ||
      !isWithinWorkspaceViewCodePointLimit(query, 200) ||
      !Number.isInteger(limit) ||
      limit < 1 ||
      limit > 12
    )
      return [];
    const generation = quick.queryGeneration$.value;
    if (generation === null) return [];
    const ids: string[] = [];
    let visited = 0;
    for (const item of quick.items$.value) {
      if (visited++ >= 128 || ids.length >= limit) break;
      if (item.disabled) continue;
      if (item.source === 'commands') {
        const command = item.payload as AffineCommand | undefined;
        if (
          command?.navigation &&
          command.navigation.definition.capability !== 'workspace-search'
        )
          ids.push(command.id);
      } else if (
        isWithinWorkspaceViewCodePointLimit(item.id, 256) &&
        this.isContentNavigable(item)
      ) {
        const id = navigationActionId(
          scope,
          QUICK_SEARCH_RESULT_OWNER,
          quickSearchResultDefinitionId(generation, item.id)
        );
        if (context.navigation.resolveParent(scope, id).ok) ids.push(id);
      }
    }
    return context.navigation.describeActions(scope, ids);
  }

  async executeDiscovery(
    request: WorkspaceNavigationRequest,
    signal?: AbortSignal
  ): Promise<WorkspaceNavigationOutcome> {
    const context = this.framework.get(WorkspaceViewContextService);
    const fail = (
      status: 'blocked' | 'stale' | 'timeout'
    ): WorkspaceNavigationOutcome => ({
      status,
      messageKey: mapWorkspaceContextIssueToMessageKey(status),
    });
    const resolved = context.navigation.resolve(request);
    if (!resolved.ok) return fail(resolved.reason);
    if (resolved.definition.capability !== 'workspace-search')
      return fail('blocked');
    const query = request.args.query;
    const limit = request.args.limit ?? 12;
    if (
      typeof query !== 'string' ||
      !isWithinWorkspaceViewCodePointLimit(query, 200) ||
      typeof limit !== 'number' ||
      !Number.isInteger(limit) ||
      limit < 1 ||
      limit > 12
    )
      return fail('blocked');
    const quick = this.quickSearchService.quickSearch;
    const before = quick.queryGeneration$.value;
    // Invocation owns the actual visible button, cursor animation and abort guard.
    const outcome = await context.navigationPort.invoke(request, signal);
    if (outcome.status !== 'reached') return outcome;
    if (
      signal?.aborted ||
      !context.isActiveScope(request) ||
      !context.navigation.resolveParent(request, request.actionId).ok
    )
      return fail('stale');
    if (
      !quick.show$.value ||
      quick.query$.value !== query ||
      quick.queryGeneration$.value === before
    )
      return fail('stale');
    const generation = quick.queryGeneration$.value;
    const ready = await new Promise<'ready' | 'stale' | 'timeout'>(resolve => {
      const cleanups: Array<() => void> = [];
      let done = false;
      let onlineActionTaken = false;
      let activatingOnline = false;
      const finish = (value: 'ready' | 'stale' | 'timeout') => {
        if (done) return;
        done = true;
        cleanups.splice(0).forEach(cleanup => cleanup());
        resolve(value);
      };
      const check = () => {
        if (done || activatingOnline) return;
        if (
          signal?.aborted ||
          !context.isActiveScope(request) ||
          !context.navigation.resolveParent(request, request.actionId).ok ||
          !quick.show$.value ||
          quick.query$.value !== query ||
          quick.queryGeneration$.value !== generation
        )
          finish('stale');
        else if (!(quick.isLoading$.value || !quick.isSettled$.value)) {
          const footer = quick.footer$.value;
          if (footer?.busy) return;
          if (footer?.action && !onlineActionTaken) {
            onlineActionTaken = true;
            activatingOnline = true;
            try {
              // The local list may already have a render ACK for this query.
              // The visible online action changes that list within the same generation.
              this.contentResultsGeneration$.next(null);
              footer.action();
            } finally {
              activatingOnline = false;
            }
          }
          let hasContent = false,
            visited = 0;
          for (const item of quick.items$.value) {
            if (visited++ >= 128) break;
            if (this.isContentNavigable(item)) {
              hasContent = true;
              break;
            }
          }
          const rendered = this.contentResultsGeneration$.value;
          if (
            hasContent &&
            (!rendered ||
              rendered.generation !== generation ||
              rendered.scope.workspaceId !== request.workspaceId ||
              rendered.scope.viewId !== request.viewId)
          )
            return;
          finish('ready');
        }
      };
      const timer = setTimeout(() => finish('timeout'), 15000);
      cleanups.push(() => clearTimeout(timer));
      const abort = () => finish('stale');
      signal?.addEventListener('abort', abort);
      cleanups.push(() => signal?.removeEventListener('abort', abort));
      const sources: Observable<unknown>[] = [
        quick.isLoading$,
        quick.isSettled$,
        quick.footer$,
        quick.queryGeneration$,
        this.contentResultsGeneration$,
        context.changes$,
      ];
      for (const source$ of sources) {
        if (done) break;
        const subscription = source$.subscribe(check);
        if (done) subscription.unsubscribe();
        else cleanups.push(() => subscription.unsubscribe());
      }
      check();
    });
    if (ready !== 'ready') return fail(ready);
    const actions = await this.searchNavigable(query, request, limit);
    const current = context.readActive();
    if (
      signal?.aborted ||
      !quick.show$.value ||
      quick.isLoading$.value ||
      !quick.isSettled$.value ||
      quick.query$.value !== query ||
      quick.queryGeneration$.value !== generation ||
      !current.ok ||
      !context.isActiveScope(request) ||
      !context.navigation.resolveParent(request, request.actionId).ok
    )
      return fail('stale');
    const search = current.snapshot.actions.find(
      action =>
        action.actionId === request.actionId &&
        action.capability === 'workspace-search'
    );
    if (!search) return fail('stale');
    const fitted = fitWorkspaceViewSnapshot({
      ...current.snapshot,
      actions: [search, ...actions],
    });
    if (!fitted.ok) return fail('blocked');
    return {
      ...outcome,
      context: fitted.snapshot,
      target: { ...outcome.target, revision: fitted.snapshot.revision },
      results: fitted.snapshot.actions.filter(
        action => action.actionId !== search.actionId
      ),
    };
  }

  close() {
    this.quickSearchService.personFilter$.next(null);
    this.advancedSearchService.close();
    if (this.quickSearchService.quickSearch.show$.value) {
      this.quickSearchService.quickSearch.hide();
    }
  }

  /**
   * Closes the search while it still shows `query`: the search the assistant ran and nobody has
   * taken over since. A search the user typed into in the meantime is theirs and stays.
   */
  closeSearchFor(query: string) {
    const quick = this.quickSearchService.quickSearch;
    if (quick.show$.value && quick.query$.value === query) this.close();
  }

  openCompact(
    defaultQuery = '',
    {
      collectionsEnabled = false,
      tagsEnabled = false,
    }: CMDKQuickSearchProductFeatures = {}
  ) {
    this.advancedSearchService.close();
    const recentDocs = this.framework.createEntity(
      RecentDocsQuickSearchSession
    );
    const docs = this.framework.createEntity(DocsQuickSearchSession);
    docs.listsPlacesOf(this.quickSearchService.placeSource$);
    const commands = this.framework.createEntity(CommandsQuickSearchSession);
    // A doc found by its content is not repeated as a sidebar entry, and no
    // sidebar entry of a doc shows before the local answer is listed.
    commands.showsDocsElsewhere(
      LiveData.computed(get =>
        get(docs.isSettled$)
          ? shownDocIds([...get(recentDocs.items$), ...get(docs.items$)])
          : null
      )
    );
    this.quickSearchService.quickSearch.show(
      [
        recentDocs,
        collectionsEnabled
          ? this.framework.createEntity(CollectionsQuickSearchSession)
          : null,
        commands,
        this.framework.createEntity(CreationQuickSearchSession),
        docs,
        this.framework.createEntity(MailQuickSearchSession),
        this.framework.createEntity(PeopleQuickSearchSession),
        this.framework.createEntity(LinksQuickSearchSession),
        tagsEnabled
          ? this.framework.createEntity(TagsQuickSearchSession)
          : null,
      ].filter((session): session is NonNullable<typeof session> => !!session),
      result => {
        if (!result) return;
        this.advancedSearchService.close();
        this.activateResult(result, { collectionsEnabled, tagsEnabled });
      },
      {
        placeholder: {
          i18nKey: 'com.streamlain.cmdk.docs.placeholder',
        },
        defaultQuery,
        allowAdvanced: true,
      }
    );
  }
  /** Change presentation without replacing the active matcher/result sessions. */
  openAdvanced(
    defaultQuery = '',
    features: CMDKQuickSearchProductFeatures = {}
  ) {
    const quick = this.quickSearchService.quickSearch;
    if (quick.show$.value) quick.setQuery(defaultQuery);
    else this.openCompact(defaultQuery, features);
    this.advancedSearchService.open(defaultQuery);
  }

  /** The ordinary result owner also serves advanced search and split previews. */
  activateResult(
    result: QuickSearchItem,
    {
      collectionsEnabled = false,
      tagsEnabled = false,
    }: CMDKQuickSearchProductFeatures = {},
    inSplit = false
  ) {
    // An opened hit takes the room: the workspace sidebar folds away for it.
    if (result.source !== 'commands' || openedDocId(result)) {
      requestWorkspacePaneSidebar('workspace', 'close');
    }
    if (isPersonDocItem(result)) {
      openDocAtPerson(
        this.workbenchService.workbench,
        result.payload.docId,
        result.payload.userId,
        inSplit
      );
      this.navigationPanelRevealService.revealDoc(result.payload.docId, {
        source: inSplit ? 'advanced-search' : 'compact-search',
        workspaceId: this.workspaceService.workspace.id,
      });
      return;
    }
    if (result.source === 'commands') {
      result.payload.run()?.catch((err: unknown) => {
        console.error(err);
      });
      return;
    }

    if (result.source === 'link') {
      const { docId, blockIds, elementIds, mode } = result.payload;
      this.workbenchService.workbench.openDoc(
        {
          docId,
          blockIds,
          elementIds,
          mode,
          // refreshKey forces focusAt$ to re-emit even for same-doc navigation
          refreshKey: nanoid(8),
        },
        inSplit ? { at: 'beside' } : undefined
      );
      this.navigationPanelRevealService.revealDoc(docId, {
        source: inSplit ? 'advanced-search' : 'compact-search',
        workspaceId: this.workspaceService.workspace.id,
      });
      return;
    }

    if (result.source === 'mail') {
      this.workbenchService.workbench.open(
        inSplit
          ? toFocusedMailMessageRoute(result.payload.route)
          : result.payload.route,
        inSplit ? { at: 'beside' } : undefined
      );
      return;
    }

    if (result.source === 'recent-doc' || result.source === 'docs') {
      const payload = result.payload as DocsSearchNavigablePayload;
      let skippedMissingBlock: {
        blockId: string;
        docId: string;
        sourceType: string | undefined;
      } | null = null;
      const resolvers = createDocsSearchNavigationResolvers(this.docsService);

      const navigation = buildDocsSearchNavigation(payload, {
        ...resolvers,
        resolveBlockStatus: (docId, blockId) => {
          const status = resolvers.resolveBlockStatus(docId, blockId);
          if (status === 'missing') {
            skippedMissingBlock = {
              blockId,
              docId,
              sourceType: payload.sourceType,
            };
          }
          return status;
        },
      });
      if (!navigation) {
        if (skippedMissingBlock) {
          console.warn(
            '[CMDKQuickSearchService] Skipping stale search result for missing block',
            skippedMissingBlock
          );
        }
        return;
      }

      result.source === 'recent-doc' && track.$.cmdk.recent.recentDocs();
      result.source === 'docs' && track.$.cmdk.results.searchResultsDocs();

      const person = this.quickSearchService.personFilter$.value;
      if (person)
        openDocAtPerson(
          this.workbenchService.workbench,
          navigation.docId,
          person.id,
          inSplit
        );
      else
        openDocsSearchNavigation(
          this.workbenchService.workbench,
          navigation,
          inSplit ? { at: 'beside' } : undefined
        );
      this.navigationPanelRevealService.revealDoc(navigation.docId, {
        source: inSplit ? 'advanced-search' : 'compact-search',
        workspaceId: this.workspaceService.workspace.id,
      });
      return;
    }

    if (result.source === 'collections') {
      if (!collectionsEnabled) {
        return;
      }
      this.workbenchService.workbench.openCollection(
        result.payload.collectionId
      );
      return;
    }

    if (result.source === 'tags') {
      if (!tagsEnabled) {
        return;
      }
      this.workbenchService.workbench.openTag(result.payload.tagId);
      return;
    }

    if (result.source === 'creation') {
      if (result.id === 'creation:create-page') {
        const newDoc = this.docsService.createDoc({
          primaryMode: 'page',
          title: result.payload.title,
          createInWorkspaceRoot: true,
          userInitiated: true,
        });

        this.workbenchService.workbench.openDoc(newDoc.id);
        this.navigationPanelRevealService.revealDoc(newDoc.id, {
          source: inSplit ? 'advanced-search' : 'compact-search',
          workspaceId: this.workspaceService.workspace.id,
        });
      } else if (result.id === 'creation:create-edgeless') {
        const newDoc = this.docsService.createDoc({
          primaryMode: 'edgeless',
          title: result.payload.title,
          createInWorkspaceRoot: true,
          userInitiated: true,
        });
        this.workbenchService.workbench.openDoc(newDoc.id);
        this.navigationPanelRevealService.revealDoc(newDoc.id, {
          source: inSplit ? 'advanced-search' : 'compact-search',
          workspaceId: this.workspaceService.workspace.id,
        });
      }
      return;
    }
  }
}

/** Docs that content rows already show, by the doc each row opens. */
function shownDocIds(items: readonly QuickSearchItem[]): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const item of items) {
    const docId = (item.payload as { docId?: unknown } | undefined)?.docId;
    if (typeof docId === 'string') ids.add(docId);
  }
  return ids;
}

/** The doc a sidebar entry in the results opens, if it opens one. */
export function openedDocId(item: QuickSearchItem): string | undefined {
  if (item.source !== 'commands') return undefined;
  return (item.payload as AffineCommand).navigation?.definition.docId;
}

function toFocusedMailMessageRoute(route: string) {
  const [pathAndSearch = '', hash = ''] = route.split('#', 2);
  const [pathname = '', search = ''] = pathAndSearch.split('?', 2);
  const params = new URLSearchParams(search);
  params.set('view', 'message');
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
}
